import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import { StatementsFormVM } from 'front-office/view-models/statements/statements-form';
import { DateTime } from 'luxon';
import { RequestCertificateGeneric } from 'shell/models/request-generic';

export const transformRequestStatementsData = (
  values: StatementsFormVM
): RequestCertificateGeneric => {
  const hasGenerality =
    (values.generality &&
      (values.applicant?.fiscalCode || values.applicant?.name || values.applicant?.surname)) ||
    values.applicant;

  const generalitaRichiedente = hasGenerality
    ? {
        docRiconoscimento:
          values.generality?.dateReleaseDocument || values.generality?.numberDocument
            ? {
                dataRilascio: values.generality?.dateReleaseDocument?.toFormat('yyyy-MM-dd'),
                estDocRiconoscimento: values.generality?.numberDocument,
              }
            : undefined,
        generalita: hasGenerality
          ? {
              idSchedaSoggettoANPR: values.generality?.idSubjectAnpr || undefined,
              codiceFiscale: values.applicant?.fiscalCode
                ? {
                    codFiscale: values.applicant?.fiscalCode,
                  }
                : undefined,
              nome: values.applicant?.name,
              cognome: values.applicant?.surname,
              senzaNome: values.applicant?.withoutName ? '1' : undefined,
              senzaCognome: values.applicant?.withoutSurname ? '1' : undefined,
              sesso: values.applicant?.gender,
              dataNascita: values.generality?.birthDate?.toFormat('yyyy-MM-dd'),
              senzaGiorno:
                !values.generality?.birthChoiceDate?.includes('day') &&
                values.generality?.birthChoiceDate?.includes('month')
                  ? '1'
                  : undefined,
              senzaGiornoMese:
                !values.generality?.birthChoiceDate?.includes('day') &&
                !values.generality?.birthChoiceDate?.includes('month') &&
                values.generality?.birthDate
                  ? '1'
                  : undefined,
              luogoNascita:
                values.generality?.municipalityOfBirth || values.generality?.foreignStateBirth
                  ? {
                      comune:
                        !values.generality?.isForeign && values.generality?.municipalityOfBirth
                          ? {
                              nomeComune: values.generality?.municipalityOfBirth?.description,
                              siglaProvinciaIstat: values.generality?.municipalityOfBirth?.province,
                              descrizioneLocalita: values.generality?.locality,
                            }
                          : undefined,
                      localita:
                        values.generality?.isForeign && values.generality?.foreignStateBirth
                          ? {
                              codiceStato: values.generality?.foreignStateBirth?.id,
                              descrizioneStato: values.generality?.foreignStateBirth?.value,
                              provinciaContea: values.generality?.foreignStateBirth?.province,
                              descrizioneLocalita:
                                values.generality?.foreignMunicipality?.description,
                            }
                          : undefined,
                    }
                  : undefined,
            }
          : undefined,
      }
    : undefined;

  const generalitaIntestatario =
    (values.generality &&
      (values.applicant?.fiscalCode || values.applicant?.name || values.applicant?.surname)) ||
    values.applicant
      ? {
          idSchedaSoggettoANPR: values.generality?.idSubjectAnpr || undefined,
          generalita: {
            codiceFiscale: values.applicant?.fiscalCode
              ? {
                  codFiscale: values.applicant?.fiscalCode,
                }
              : undefined,
            nome: values.applicant?.name,
            cognome: values.applicant?.surname,
            senzaNome: values.applicant?.withoutName ? '1' : undefined,
            senzaCognome: values.applicant?.withoutSurname ? '1' : undefined,
            sesso: values.applicant?.gender,
            dataNascita: values.generality?.birthDate?.toFormat('yyyy-MM-dd'),
            senzaGiorno:
              !values.generality?.birthChoiceDate?.includes('day') &&
              values.generality?.birthChoiceDate?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !values.generality?.birthChoiceDate?.includes('day') &&
              !values.generality?.birthChoiceDate?.includes('month') &&
              values.generality?.birthDate
                ? '1'
                : undefined,
            luogoNascita:
              values.generality?.municipalityOfBirth || values.generality?.foreignStateBirth
                ? {
                    comune:
                      !values.generality?.isForeign && values.generality?.municipalityOfBirth
                        ? {
                            nomeComune: values.generality?.municipalityOfBirth?.description,
                            siglaProvinciaIstat: values.generality?.municipalityOfBirth?.province,
                            descrizioneLocalita: values.generality?.locality,
                          }
                        : undefined,
                    localita:
                      values.generality?.isForeign && values.generality?.foreignStateBirth
                        ? {
                            codiceStato: values.generality?.foreignStateBirth?.id,
                            descrizioneStato: values.generality?.foreignStateBirth?.value,
                            provinciaContea: values.generality?.foreignStateBirth?.province,
                            descrizioneLocalita:
                              values.generality?.foreignMunicipality?.description,
                          }
                        : undefined,
                  }
                : undefined,
          },
        }
      : undefined;

  const cdCertificato = values.statements
    ? `DS${values.statements.key.codiceDocumento}`
    : undefined;

  const indirizzoResidenzaIntestatario = values.generality?.addressString || '';

  const comuneResidenzaIntestatario = values.generality?.residenceString || '';

  let cartaIdentitaComuneRilascio = '';

  if (values.generality?.isForeignDocument === DocumentRadioCertificate.Other) {
    cartaIdentitaComuneRilascio = values.generality?.consulateCode || '';
  } else if (values.generality?.isForeignDocument === DocumentRadioCertificate.IdentityCard) {
    cartaIdentitaComuneRilascio =
      `Comune di ${values.generality?.municipalityDocument?.description}` || '';
  }

  const numeroCarta =
    values.generality?.isFromAnpr &&
    values.generality?.isForeignDocument === DocumentRadioCertificate.IdentityCard
      ? `Carta d'identità n.${values.generality?.numberDocument || ''}`
      : values.generality?.numberDocument || '';

  const documentoIdentita = `${numeroCarta || ''} rilasciato il ${
    values.generality?.dateReleaseDocument
      ? values.generality?.dateReleaseDocument?.toFormat('dd/MM/yyyy')
      : ''
  } da ${cartaIdentitaComuneRilascio}`;

  let provinciaResidenzaIntestatario = '';

  if (values?.generality?.residence?.indirizzo) {
    provinciaResidenzaIntestatario =
      values.generality?.residence?.indirizzo?.comune?.siglaProvinciaIstat || '';
  } else if (values?.generality?.residence?.localitaEstera) {
    provinciaResidenzaIntestatario =
      values.generality?.residence?.localitaEstera?.indirizzoEstero?.localita?.provinciaContea ||
      '';
  }

  let eredi = undefined;

  if (values.heirsNote && values.statements?.key?.codiceDocumento === '4') {
    eredi = [{ datiErede: values.heirsNote || '' }];
  }

  let eredeTestamento = undefined;

  if (values.will?.heirData && values.statements?.key.codiceDocumento === '3') {
    eredeTestamento = values.will?.heirData;
  }

  return {
    generalitaRichiedente,
    cdCertificato,
    motivoRilascio: values.accountholder?.account?.applicant?.releaseReason || undefined,
    usoInterno: values?.certificateInternOnly,
    cognomeIntestatario: values.applicant?.surname,
    nomeIntestatario: values.applicant?.name,
    dataNascitaIntestatario: values.generality?.birthDate?.toFormat('dd/MM/yyyy'),
    luogoNascitaIntestatario: !values.generality?.isForeign
      ? values.generality?.municipalityOfBirth?.description
      : values.generality?.foreignMunicipality?.description,
    cittadino: values.applicant?.gender,
    // comune residenza, provincia residenza, indirizzo residenza
    comuneResidenzaIntestatario,
    provinciaResidenzaIntestatario,
    indirizzoResidenzaIntestatario,
    // cittadiniTitoloCertificato ????
    testoDichiarazione:
      values.statements?.key.codiceDocumento === '1'
        ? values.declaration
        : values.statements?.key.codiceDocumento === '5'
        ? values.textDeclaration
        : '',
    // idFamigliaConvivenzaANPR
    idFamigliaConvivenzaANPR: values.generality?.idFamilyAnpr,
    cartaIdentitaNRIntestatario: numeroCarta,
    cartaIdentitaRilasciataIl: values.generality?.dateReleaseDocument?.toFormat('dd/MM/yyyy'),
    cartaIdentitaComuneRilascio: cartaIdentitaComuneRilascio,
    generalitaIntestatario: generalitaIntestatario?.generalita,
    documentoIdentita,
    // decedeuto
    cognomeConiugeDeceduto: values.decased?.surname,
    nomeConiugeDeceduto: values.decased?.name,
    dataNascitaDeceduto: values.decased?.dateBirth?.toFormat('dd/MM/yyyy'),
    comuneNascitaDeceduto: values.decased?.municipalityOfBirth?.description,
    dataDecesso: values.decased?.dateDeath?.toFormat('dd/MM/yyyy'),
    comuneDecesso: values.decased?.municipalityOfDeath?.description,
    comuneMatrimonio: values.decased?.municipalityOfMarriage?.description,
    dataMatrimonio: values.decased?.dateMarriage?.toFormat('dd/MM/yyyy'),
    sessoConiugeDeceduto: values.decased?.gender,
    // testamento
    tipoTestamento: values.will?.isOlografo, // TODO
    cognomeNotaio: values.will?.surnameNotary,
    nomeNotaio: values.will?.nameNotary,
    comuneStudioNotarile: values.will?.municipalityNotary?.description,
    repertorioNr: values.will?.repertoire || undefined,
    dataRegistrazioneTestamento: values.will?.dateWill?.toFormat('dd/MM/yyyy'),
    unicoEredetitoloUniversale: values.will?.isUnicoErede ? 'S' : 'N',
    // cognomeErede: values.will?.heirData,
    // nomeErede: ' ',
    eredeTestamento,
    // Dati erede
    eredi,
    // Figli
    figlio: values.sons?.map((son) => ({
      cognomeFiglio: son.surname,
      comuneResidenzaFiglio:
        son.italianMunicipalityResidence?.description ||
        son.foreignMunicipalityResidence?.description,
      dataNascitaFiglio: son.dateBirth?.toFormat('dd/MM/yyyy'),
      indirizzoResidenzaFiglio: son.addressResidence, // TODO
      luogoNascitaFiglio: son.municipalityOfBirth?.description, // TODO
      nomeFiglio: son.name,
      provinciaResidenzaFiglio:
        son.italianMunicipalityResidence?.province || son.foreignMunicipalityResidence?.province,
      sessoFiglio: son.gender,
      minoreFiglio: (son.dateBirth?.diff(DateTime.now(), 'years')?.years || 0) >= 18 ? 'S' : 'N',
    })),
    dichiarazione: values.declarationAdded
      ? [{ dichiarazioneAggiuntiva: values.declarationAdded || '' }]
      : undefined,
    formulaTestamento: values.declarationFormula,
    codiceStato: values.generality?.idState,
    //Type payment
    tipoPagamento: values.typePayment,
    tipoDocumento: values.statements,
    // tipo indirizzo
    tipoIndirizzo: values.generality?.residence?.tipoIndirizzo,
    // modalita rilascio
    codiceModalitaRilascio: values.modality?.id as string,
  };
};
